import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { speakersApi } from '../lib/api';
import { toast } from 'sonner';
import { Plus, Trash2, User, Edit2, Check, X, Camera } from 'lucide-react';
import ImageUpload from './ImageUpload';

export const SpeakersDialog = ({ open, onOpenChange, eventId, speakers, onSpeakersChange }) => {
  const [newSpeaker, setNewSpeaker] = useState({ name: '', role: '', avatar_url: '' });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: '', role: '', avatar_url: '' });
  const [adding, setAdding] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newSpeaker.name.trim()) return;

    setAdding(true);
    try {
      const res = await speakersApi.create(eventId, newSpeaker);
      onSpeakersChange([...speakers, res.data]);
      setNewSpeaker({ name: '', role: '', avatar_url: '' });
      toast.success('Ponente agregado');
    } catch (error) {
      toast.error('Error al agregar ponente');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdate = async (speakerId) => {
    try {
      const res = await speakersApi.update(eventId, speakerId, editData);
      onSpeakersChange(speakers.map(s => s.id === speakerId ? res.data : s));
      setEditingId(null);
      toast.success('Ponente actualizado');
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const handleDelete = async (speakerId) => {
    if (!window.confirm('¿Eliminar este ponente?')) return;
    try {
      await speakersApi.delete(eventId, speakerId);
      onSpeakersChange(speakers.filter(s => s.id !== speakerId));
      toast.success('Ponente eliminado');
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const startEdit = (speaker) => {
    setEditingId(speaker.id);
    setEditData({
      name: speaker.name,
      role: speaker.role || '',
      avatar_url: speaker.avatar_url || ''
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">Gestionar ponentes</DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          {/* Add new speaker */}
          <form onSubmit={handleAdd} className="space-y-3 p-4 border border-dashed rounded-lg">
            <div className="text-sm font-medium text-muted-foreground">Nuevo ponente</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Nombre *</Label>
                <Input
                  placeholder="Nombre completo"
                  value={newSpeaker.name}
                  onChange={(e) => setNewSpeaker({ ...newSpeaker, name: e.target.value })}
                  data-testid="new-speaker-name"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Rol / Cargo</Label>
                <Input
                  placeholder="Ej: CEO @ Company"
                  value={newSpeaker.role}
                  onChange={(e) => setNewSpeaker({ ...newSpeaker, role: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Foto</Label>
              <ImageUpload
                value={newSpeaker.avatar_url}
                onChange={(url) => setNewSpeaker({ ...newSpeaker, avatar_url: url })}
                placeholder="Subir foto"
              />
            </div>
            <Button type="submit" size="sm" disabled={adding || !newSpeaker.name.trim()} className="w-full" data-testid="add-speaker-btn">
              <Plus className="w-4 h-4 mr-2" />
              Agregar ponente
            </Button>
          </form>

          {/* Speakers list */}
          <ScrollArea className="h-[300px]">
            {speakers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <User className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No hay ponentes aún</p>
              </div>
            ) : (
              <div className="space-y-2">
                {speakers.map(speaker => (
                  <div
                    key={speaker.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors"
                    data-testid={`speaker-item-${speaker.id}`}
                  >
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-sm font-medium flex-shrink-0 overflow-hidden">
                      {speaker.avatar_url ? (
                        <img src={speaker.avatar_url} alt={speaker.name} className="w-full h-full object-cover" />
                      ) : (
                        speaker.initials
                      )}
                    </div>

                    {editingId === speaker.id ? (
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={editData.name}
                            onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                            placeholder="Nombre"
                            className="h-8"
                          />
                          <Input
                            value={editData.role}
                            onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                            placeholder="Rol"
                            className="h-8"
                          />
                        </div>
                        <ImageUpload
                          value={editData.avatar_url}
                          onChange={(url) => setEditData({ ...editData, avatar_url: url })}
                          placeholder="Cambiar foto"
                        />
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => handleUpdate(speaker.id)}>
                            <Check className="w-4 h-4 text-green-500" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => setEditingId(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{speaker.name}</div>
                          {speaker.role && (
                            <div className="text-xs text-muted-foreground truncate">{speaker.role}</div>
                          )}
                        </div>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(speaker)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDelete(speaker.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SpeakersDialog;
